import React, { useContext, useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import SuccessLiveIcon from "../../resources/icons/success-live.svg";
import ErrorLiveIcon from "../../resources/icons/error-live-icon.svg";
import AuthContext from "../../store/auth-context";

const LoadingContent = () => (
  <div className="text-center">
    <svg
      aria-hidden="true"
      className="inline w-12 h-12 text-gray-200 animate-spin fill-[#3538CD]"
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0492C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentFill"
      />
    </svg>
    <p className="mt-4 text-xl font-semibold text-gray-700">
      Verifying your account...
    </p>
    <p className="text-gray-500">Please wait a moment.</p>
  </div>
);

const ActivateAccount = () => {
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const { uuid, token } = useParams();

  const authContext = useContext(AuthContext);

  useEffect(() => {
    if (uuid && token) {
      const activate = async (uuid, token) => {
        try {
          const response = await GET(
            `${EntityConstants.ACTIVATE_ACCOUNT}/${uuid}/${token}/`
          );
          const data = await response.json();

          if (response.ok) {
            setMessage(
              data.message || "Your account has been successfully activated."
            );
            setIsError(false);
          } else {
            setMessage(
              data.message ||
                "Sorry, we couldn't activate your account. The link may be invalid or expired."
            );
            setIsError(true);
            console.error("Activation Error:", data);
          }
        } catch (error) {
          setMessage(
            "An unexpected error occurred. Please try again later or contact support."
          );
          setIsError(true);
          console.error("Activation Failed:", error);
        } finally {
          setIsLoading(false);
        }
      };

      activate(uuid, token);
    } else {
      setMessage(
        "Invalid activation link. Please check the URL and try again."
      );
      setIsError(true);
      setIsLoading(false);
    }
  }, [uuid, token]);

  const handleSignInClick = () => {
    localStorage.setItem("redirectAfterLogin", "/issuer/discover");
    authContext.chooseSignUp_SignIn(true);
    authContext.setTriggerLoginModuleToggleValue(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-lg text-center">
        {isLoading ? (
          <LoadingContent />
        ) : (
          <>
            <div className="flex justify-center">
              {isError ? (
                <img src={ErrorLiveIcon} alt="Activation error" />
              ) : (
                <img src={SuccessLiveIcon} alt="Activation success" />
              )}
            </div>

            <h1
              className={`mt-4 text-3xl font-bold ${
                isError ? "text-red-600" : "text-gray-800"
              }`}
            >
              {isError ? "Activation Failed" : "Account Activated!"}
            </h1>

            <p className="mt-2 text-gray-600">{message}</p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              {!isError && (
                <button
                  className="w-full sm:w-auto text-md font-medium text-white bg-[#3538CD] hover:bg-blue-700 focus:outline-none focus:ring-4 py-2 px-3 rounded-[8px] transition-colors duration-300"
                  onClick={handleSignInClick}
                >
                  Sign In Now
                </button>
              )}
              <span className="text-gray-600 font-semibold">OR</span>
              <NavLink
                to={"/"}
                className="w-full hover:no-underline sm:w-auto text-md font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-300 py-2 px-3 rounded-[8px] transition-colors duration-300"
              >
                Go to Home
              </NavLink>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivateAccount;
